import type {
  ChartSyllable,
  ChartTone,
} from "../content/pinyin-chart/pinyin-chart.js";
import type { ChartVoice } from "../content/pinyin-chart/pinyin-chart-voices.js";

/** One question: a recording to hear and the tone it is in. */
export interface ToneQuestion {
  readonly syllable: ChartSyllable;
  readonly tone: ChartTone;
  readonly voice: ChartVoice;
}

/** The highest HSK band a quiz syllable's commonest character may be graded at. */
const commonestBand = 4;

/**
 * The tones a quiz asks about: recorded, and read on a character a learner
 * will meet early.
 *
 * A question about `zhuǎi` tests whether a reader has heard of zhuai. The
 * point is the tone, so every question is a syllable a learner in the first
 * four HSK bands already reads, whose tone they are learning to hear.
 * @param syllables - The chart's syllables.
 * @returns The syllable and tone of each possible question.
 */
export function quizPool(
  syllables: readonly ChartSyllable[],
): readonly Omit<ToneQuestion, "voice">[] {
  return syllables.flatMap((syllable) =>
    syllable.rare
      ? []
      : syllable.tones
          .filter((tone) => {
            const band = Number(tone.examples[0]?.band ?? Number.NaN);

            return tone.audio !== undefined && band <= commonestBand;
          })
          .map((tone) => ({ syllable, tone })),
  );
}

/** How many syllables a round asks unless a caller says otherwise. */
export const roundLength = 10;

/**
 * A listening quiz: hear a syllable, say which tone it was in.
 *
 * A round is {@link roundLength} questions from {@link quizPool}, in a random
 * order and either voice, and a question is never asked twice in a row. Each
 * question takes one answer. A second is ignored, so a reader who has been
 * shown the right tone cannot change theirs to it. The score is the round's
 * and nothing is stored.
 */
export class ToneQuiz {
  readonly #pool: readonly Omit<ToneQuestion, "voice">[];
  readonly #random: () => number;
  readonly #length: number;
  #current: ToneQuestion | undefined;
  #answered = false;
  #asked = 0;
  #right = 0;
  #firstRight = false;

  /**
   * @param pool - What may be asked.
   * @param random - A number in [0, 1), which a test fixes.
   * @param length - How many questions a round asks.
   */
  constructor(
    pool: readonly Omit<ToneQuestion, "voice">[],
    random: () => number = Math.random,
    length: number = roundLength,
  ) {
    this.#pool = pool;
    this.#random = random;
    this.#length = length;
  }

  /** The question being asked, if one is. */
  get current(): ToneQuestion | undefined {
    return this.#current;
  }

  /** Whether the current question has been answered. */
  get answered(): boolean {
    return this.#answered;
  }

  /** How many questions a round asks. */
  get length(): number {
    return this.#length;
  }

  /** Which question of the round is being asked, counting from 1. */
  get number(): number {
    return this.#asked + (this.#answered ? 0 : 1);
  }

  /** Whether the last question of the round has been answered. */
  get finished(): boolean {
    return this.#answered && this.#asked >= this.#length;
  }

  /** How many have been answered this round, and how many were right. */
  get score(): { readonly asked: number; readonly right: number } {
    return { asked: this.#asked, right: this.#right };
  }

  /**
   * Begin a round, forgetting the last one's score.
   * @returns The first question, or undefined for an empty pool.
   */
  start(): ToneQuestion | undefined {
    this.#asked = 0;
    this.#right = 0;
    this.#current = undefined;
    this.#answered = false;

    return this.next();
  }

  /**
   * Ask the round's next question.
   * @returns The question, or undefined for an empty pool or a finished round.
   */
  next(): ToneQuestion | undefined {
    if (this.#pool.length === 0 || this.#asked >= this.#length) {
      return undefined;
    }

    let question = this.#pick();

    for (
      let tries = 0;
      tries < 5 && question.tone.key === this.#current?.tone.key;
      tries++
    ) {
      question = this.#pick();
    }

    this.#current = question;
    this.#answered = false;

    return question;
  }

  /**
   * Answer the current question. A second answer to it changes nothing.
   * @param tone - The tone the reader chose, 1 to 4.
   * @returns Whether the first answer was right, or undefined with no
   * question asked.
   */
  answer(tone: number): boolean | undefined {
    if (this.#current === undefined) {
      return undefined;
    }

    if (!this.#answered) {
      this.#answered = true;
      this.#asked += 1;
      this.#firstRight = this.#current.tone.tone === tone;
      this.#right += this.#firstRight ? 1 : 0;
    }

    return this.#firstRight;
  }

  #pick(): ToneQuestion {
    const at = Math.floor(this.#random() * this.#pool.length);
    const found = this.#pool[Math.min(at, this.#pool.length - 1)];

    if (found === undefined) {
      throw new Error("The quiz has nothing to ask");
    }

    return { ...found, voice: this.#random() < 0.5 ? "female" : "male" };
  }
}
